import * as React from "react";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  IconButton,
  LinearProgress,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import { DataGrid } from "@mui/x-data-grid";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  deleteCenter,
  getCenter,
  updateCenter,
} from "../../../../redux/actions/center";
import { store } from "../../../../store";
import { NoRowOverlay } from "../../../table/NoRowOverlay";
import PageHeader from "../../../ui/PageHeader";
import { getToday } from "../../../ui/utils/DateUtils";
import {
  showErrorAlert,
  showWarningAlert,
} from "../../../ui/utils/AlertUtils";
import { showSuccessToast } from "../../../../shared/ui/toast/Toast";
import { CenterForm } from "./CenterForm";
import { printCenterSummary } from "./CenterPdfGenerator";
import UpdateCenterAddressDialog from "./UpdateCenterAddressDialog";
import AddCenterPersonDialog from "./AddCenterPersonDialog";
import {
  addCenterPerson,
  removeCenterPerson,
} from "../../../../services/centerPeopleService";

const detailSectionSx = {
  bgcolor: "grey.50",
  borderRadius: 2,
  p: { xs: 2, md: 3 },
};

const emptyAddress = {
  hospital: "",
  ward: "",
  building: "",
  street: "",
  floor: "",
  zipcode: "",
  city: "",
  country: "",
};

const sampleSpecificFields = [
  "hospital",
  "ward",
  "building",
  "street",
  "floor",
];

const getContactParts = (contactName = "") => {
  const [lastName = "", ...firstNameParts] = String(contactName).split(",");

  return {
    firstName: firstNameParts.join(",").trim(),
    lastName: lastName.trim(),
  };
};

const getDetails = (center) => ({
  contactPhone: center?.contactPhone || "",
  contactEmail: center?.contactEmail || "",
  ...getContactParts(center?.contactName),
});

const getKitAddress = (center) => ({
  hospital: center?.hospital || "",
  ward: center?.ward || "",
  building: center?.building || "",
  street: center?.street || "",
  floor: center?.floor || "",
  zipcode: center?.zipcode || "",
  city: center?.city || "",
  country: center?.country || "",
});

const getSampleAddress = (center) => ({
  hospital: center?.sampleHospital || "",
  ward: center?.sampleWard || "",
  building: center?.sampleBuilding || "",
  street: center?.sampleStreet || "",
  floor: center?.sampleFloor || "",
  zipcode: center?.zipcode || "",
  city: center?.city || "",
  country: center?.country || "",
});

const addressesMatch = (kitAddress, sampleAddress) => {
  const hasKitAddress = sampleSpecificFields.some(
    (field) => Boolean(kitAddress[field]),
  );

  return (
    hasKitAddress &&
    sampleSpecificFields.every(
      (field) => kitAddress[field] === sampleAddress[field],
    )
  );
};

const getAddressLines = (address) =>
  [
    [address?.hospital, address?.ward, address?.building]
      .filter(Boolean)
      .join(", "),
    [
      address?.street,
      address?.floor,
      address?.zipcode,
      address?.city,
      address?.country,
    ]
      .filter(Boolean)
      .join(", "),
  ].filter(Boolean);

const DetailSection = ({ title, action, children }) => (
  <Paper component="section" elevation={0} sx={detailSectionSx}>
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 2,
        mb: 2,
      }}>
      <Typography variant="h6" sx={{ color: "primary.main", fontWeight: 700 }}>
        {title}
      </Typography>
      {action}
    </Box>
    {children}
  </Paper>
);

const AddressCard = ({ label, address, actions }) => {
  const addressLines = getAddressLines(address);

  return (
    <Box
      sx={{
        bgcolor: "background.paper",
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
        p: 2,
      }}>
      <Box
        sx={{
          display: "flex",
          alignItems: { xs: "flex-start", sm: "center" },
          justifyContent: "space-between",
          flexDirection: { xs: "column", sm: "row" },
          gap: 1,
        }}>
        <Typography
          variant="subtitle1"
          sx={{ color: "text.primary", fontWeight: 700 }}>
          {label}
        </Typography>
        {actions}
      </Box>
      <Box sx={{ mt: 1, minHeight: 48 }}>
        {addressLines.length > 0 ? (
          addressLines.map((line) => (
            <Typography key={line} sx={{ minHeight: 24 }}>
              {line}
            </Typography>
          ))
        ) : (
          <Typography>—</Typography>
        )}
      </Box>
    </Box>
  );
};

const PeopleTable = ({ rows, columns, loading, emptyLabel }) => {
  if (!loading && rows.length === 0) {
    return (
      <Typography color="text.secondary" sx={{ py: 2, textAlign: "center" }}>
        {emptyLabel}
      </Typography>
    );
  }

  return (
    <DataGrid
      autoHeight
      disableRowSelectionOnClick
      slots={{
        noRowsOverlay: NoRowOverlay,
        loadingOverlay: LinearProgress,
      }}
      rows={rows}
      columns={columns}
      loading={loading}
      hideFooter
      rowHeight={40}
      sx={{ bgcolor: "background.paper", border: 0, borderRadius: 2 }}
    />
  );
};

export const ScreenCenterInfo = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const centerId = location.state?.centerId;
  const studyId = location.state?.studyId;
  const studyCode = location.state?.studyCode;
  const routeCenterNumber = location.state?.centerNumber;
  const centerState = useSelector((state) => state.center.center);
  const isLoadingUsers = useSelector((state) => state.user.isLoadingUsers);
  const isSaving = useSelector((state) => state.center.isLoadingCenter);
  const center =
    centerState && String(centerState.id) === String(centerId)
      ? centerState
      : null;
  const [details, setDetails] = React.useState(() => getDetails(center));
  const [kitAddress, setKitAddress] = React.useState(() =>
    getKitAddress(center),
  );
  const [sampleAddress, setSampleAddress] = React.useState(() =>
    getSampleAddress(center),
  );
  const [sameAsKitAddress, setSameAsKitAddress] = React.useState(false);
  const [changesMade, setChangesMade] = React.useState(false);
  const [centerFormOpen, setCenterFormOpen] = React.useState(false);
  const [addressMode, setAddressMode] = React.useState(null);
  const [addressDraft, setAddressDraft] = React.useState(emptyAddress);
  const [personDialogType, setPersonDialogType] = React.useState(null);
  const [personSaving, setPersonSaving] = React.useState(false);
  const [removingPerson, setRemovingPerson] = React.useState(null);

  const protocols = center?.Protocols || [];
  const centerManagers = React.useMemo(() => {
    const uniqueUsers = new Map();

    (center?.users || []).forEach((user) => {
      if (
        user?.id != null &&
        user.role === "manager" &&
        user.organization === "Center"
      ) {
        uniqueUsers.set(user.id, user);
      }
    });

    return Array.from(uniqueUsers.values());
  }, [center]);
  const monitors = center?.monitors || [];
  const peopleLoading = isLoadingUsers || (!center && isSaving);

  const peopleColumns = React.useMemo(
    () => [
      {
        field: "name",
        headerName: t("lastName"),
        flex: 1,
        minWidth: 140,
      },
      {
        field: "firstname",
        headerName: t("firstname"),
        flex: 1,
        minWidth: 140,
      },
      {
        field: "email",
        headerName: t("email"),
        flex: 1.5,
        minWidth: 200,
      },
      {
        field: "phoneNumber",
        headerName: t("phone"),
        flex: 1,
        minWidth: 150,
      },
    ],
    [t],
  );

  React.useEffect(() => {
    if (centerId) {
      store.dispatch(getCenter(centerId));
    }
  }, [centerId]);

  React.useEffect(() => {
    if (!center) {
      return;
    }

    const nextKitAddress = getKitAddress(center);
    const nextSampleAddress = getSampleAddress(center);

    setDetails(getDetails(center));
    setKitAddress(nextKitAddress);
    setSampleAddress(nextSampleAddress);
    setSameAsKitAddress(addressesMatch(nextKitAddress, nextSampleAddress));
    setChangesMade(false);
  }, [center]);

  const updateDetail = (field, value) => {
    setDetails((currentDetails) => ({
      ...currentDetails,
      [field]: value,
    }));
    setChangesMade(true);
  };

  const clearChanges = () => {
    setDetails(getDetails(center));
    setChangesMade(false);
  };

  const saveChanges = async () => {
    if (!center) {
      return;
    }

    const updated = await store.dispatch(
      updateCenter(center.id, {
        contactName: `${details.lastName.trim()}, ${details.firstName.trim()}`,
        contactPhone: details.contactPhone,
        contactEmail: details.contactEmail,
      }),
    );

    if (!updated) {
      await showErrorAlert(t("centerUpdateError"));
      return;
    }

    setChangesMade(false);
    showSuccessToast(t("changesSaved"));
  };

  const openAddressDialog = (mode) => {
    setAddressMode(mode);
    setAddressDraft(mode === "kit" ? kitAddress : sampleAddress);
  };

  const closeAddressDialog = () => {
    setAddressMode(null);
    setAddressDraft(emptyAddress);
  };

  const updateAddressDraft = (field, value) => {
    setAddressDraft((currentAddress) => ({
      ...currentAddress,
      [field]: value,
    }));
  };

  const saveAddress = async () => {
    if (!center || !addressMode) {
      return;
    }

    const payload =
      addressMode === "kit"
        ? {
            hospital: addressDraft.hospital,
            ward: addressDraft.ward,
            building: addressDraft.building,
            street: addressDraft.street,
            floor: addressDraft.floor,
            zipcode: addressDraft.zipcode,
          }
        : {
            sampleHospital: addressDraft.hospital,
            sampleWard: addressDraft.ward,
            sampleBuilding: addressDraft.building,
            sampleStreet: addressDraft.street,
            sampleFloor: addressDraft.floor,
            zipcode: addressDraft.zipcode,
          };
    const updated = await store.dispatch(updateCenter(center.id, payload));

    if (!updated) {
      await showErrorAlert(
        addressMode === "sample"
          ? t("failedToUpdateSampleAddress")
          : t("centerUpdateError"),
      );
      return;
    }

    closeAddressDialog();
    showSuccessToast(t("changesSaved"));
  };

  const handleSameAsKitAddress = async (event) => {
    const checked = event.target.checked;
    setSameAsKitAddress(checked);

    if (!checked || !center) {
      return;
    }

    const copiedSampleAddress = { ...kitAddress };
    setSampleAddress(copiedSampleAddress);

    const updated = await store.dispatch(
      updateCenter(center.id, {
        sampleHospital: kitAddress.hospital,
        sampleWard: kitAddress.ward,
        sampleBuilding: kitAddress.building,
        sampleStreet: kitAddress.street,
        sampleFloor: kitAddress.floor,
      }),
    );

    if (!updated) {
      setSameAsKitAddress(false);
      setSampleAddress(getSampleAddress(center));
      await showErrorAlert(t("failedToUpdateSampleAddress"));
      return;
    }

    showSuccessToast(t("changesSaved"));
  };

  const handleDeleteCenter = async () => {
    if (!center) {
      return;
    }

    const confirmation = await showWarningAlert(
      t("deleteCenterConfirmation"),
      {
        confirmButtonText: t("deleteConfirmButton"),
        cancelButtonText: t("cancel"),
      },
    );

    if (!confirmation.isConfirmed) {
      return;
    }

    const deleted = await store.dispatch(deleteCenter(center.id));

    if (!deleted) {
      await showErrorAlert(t("centerDeleteError"));
      return;
    }

    showSuccessToast(t("centerDeletedSuccess"));
    navigate(-1);
  };

  const handleAddCenterPerson = async (user) => {
    if (!center || !personDialogType || !user?.id) {
      return;
    }

    const currentPersonType = personDialogType;
    setPersonSaving(true);

    try {
      await addCenterPerson(center.id, currentPersonType, user.id);
      setPersonDialogType(null);
      store.dispatch(getCenter(center.id));
      showSuccessToast(
        currentPersonType === "monitor"
          ? t("monitorAddedToCenter")
          : t("managerAddedToCenter"),
      );
    } catch (error) {
      await showErrorAlert(t("addCenterPersonError"));
    } finally {
      setPersonSaving(false);
    }
  };

  const handleRemoveCenterPerson = React.useCallback(
    async (personType, user) => {
      if (!center || !user?.id) {
        return;
      }

      const displayName =
        [user.firstname, user.name].filter(Boolean).join(" ") || user.email;
      const confirmation = await showWarningAlert(
        personType === "monitor"
          ? t("removeMonitorFromCenterConfirmation", { name: displayName })
          : t("removeManagerFromCenterConfirmation", { name: displayName }),
        {
          confirmButtonText: t("remove"),
          cancelButtonText: t("cancel"),
        },
      );

      if (!confirmation.isConfirmed) {
        return;
      }

      setRemovingPerson(`${personType}-${user.id}`);

      try {
        await removeCenterPerson(center.id, personType, user.id);
        store.dispatch(getCenter(center.id));
        showSuccessToast(
          personType === "monitor"
            ? t("monitorRemovedFromCenter")
            : t("managerRemovedFromCenter"),
        );
      } catch (error) {
        await showErrorAlert(t("removeCenterPersonError"));
      } finally {
        setRemovingPerson(null);
      }
    },
    [center, t],
  );

  const managerColumns = React.useMemo(
    () => [
      ...peopleColumns,
      {
        field: "action",
        headerName: t("action"),
        width: 90,
        sortable: false,
        filterable: false,
        align: "center",
        headerAlign: "center",
        renderCell: (params) => (
          <IconButton
            aria-label={t("removeManager")}
            color="error"
            size="small"
            disabled={Boolean(removingPerson)}
            onClick={() => handleRemoveCenterPerson("manager", params.row)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        ),
      },
    ],
    [handleRemoveCenterPerson, peopleColumns, removingPerson, t],
  );

  const monitorColumns = React.useMemo(
    () => [
      ...peopleColumns,
      {
        field: "action",
        headerName: t("action"),
        width: 90,
        sortable: false,
        filterable: false,
        align: "center",
        headerAlign: "center",
        renderCell: (params) => (
          <IconButton
            aria-label={t("removeMonitor")}
            color="error"
            size="small"
            disabled={Boolean(removingPerson)}
            onClick={() => handleRemoveCenterPerson("monitor", params.row)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        ),
      },
    ],
    [handleRemoveCenterPerson, peopleColumns, removingPerson, t],
  );

  const handleDownload = () => {
    const centerNumber = center?.centerNumber || routeCenterNumber || "center";

    printCenterSummary({
      center,
      studyCode,
      protocols,
      kitAddress,
      sampleAddress,
      contact: details,
      managers: centerManagers,
      monitors,
      filename: `${studyCode ? `${studyCode}_` : ""}center_${centerNumber}_${getToday()}`,
    });
  };

  const centerNumber = center?.centerNumber || routeCenterNumber || "";
  const protocolReferences = protocols
    .map((protocol) => protocol.reference)
    .join(", ");
  const kitAddressMissing =
    center && (!center.hospital || !center.street || !center.zipcode);

  return (
    <Grid item xs={12}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        <PageHeader
          title={`${t("center")}: ${centerNumber}`}
          downloadLabel={t("download")}
          onBack={() => navigate(-1)}
          onDownload={handleDownload}
          downloadDisabled={!center}
        />

        <DetailSection
          title={t("info")}
          action={
            <IconButton
              aria-label={t("edit")}
              size="small"
              color="primary"
              disabled={!center}
              onClick={() => setCenterFormOpen(true)}>
              <EditIcon fontSize="small" />
            </IconButton>
          }>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} lg={3}>
              <TextField
                fullWidth
                label={t("centerNumber")}
                value={centerNumber}
                InputProps={{ readOnly: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <TextField
                fullWidth
                label={t("officePhone")}
                value={center?.officePhone || ""}
                InputProps={{ readOnly: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <TextField
                fullWidth
                label={t("protocols")}
                value={protocolReferences || t("initialProtocolMessage")}
                InputProps={{ readOnly: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <TextField
                fullWidth
                label={t("status")}
                value={center?.status ? t(center.status, center.status) : ""}
                InputProps={{ readOnly: true }}
              />
            </Grid>
          </Grid>
        </DetailSection>

        <DetailSection title={t("address")}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {kitAddressMissing && (
              <Box
                sx={{
                  bgcolor: "#fcf8e3",
                  borderRadius: 1,
                  color: "#8a6d3b",
                  p: 2,
                  textAlign: "center",
                }}>
                {t("fullAddressMissing")}
              </Box>
            )}

            <AddressCard
              label={t("kitOrderAddress")}
              address={kitAddress}
              actions={
                <IconButton
                  aria-label={t("edit")}
                  size="small"
                  color="primary"
                  onClick={() => openAddressDialog("kit")}>
                  <EditIcon fontSize="small" />
                </IconButton>
              }
            />

            <AddressCard
              label={t("samplePickupAddress")}
              address={sampleAddress}
              actions={
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: 1,
                  }}>
                  <FormControlLabel
                    sx={{
                      m: 0,
                      px: 1,
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 1.5,
                    }}
                    control={
                      <Checkbox
                        size="small"
                        checked={sameAsKitAddress}
                        onChange={handleSameAsKitAddress}
                      />
                    }
                    label={t("sameAsKitOrderAddress")}
                  />
                  <IconButton
                    aria-label={t("edit")}
                    size="small"
                    color="primary"
                    disabled={sameAsKitAddress}
                    onClick={() => openAddressDialog("sample")}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Box>
              }
            />
          </Box>
        </DetailSection>

        <DetailSection title={t("contact")}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} lg={3}>
              <TextField
                fullWidth
                label={t("firstname")}
                value={details.firstName}
                onChange={(event) =>
                  updateDetail("firstName", event.target.value)
                }
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <TextField
                fullWidth
                label={t("lastName")}
                value={details.lastName}
                onChange={(event) =>
                  updateDetail("lastName", event.target.value)
                }
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <TextField
                fullWidth
                type="email"
                label={t("email")}
                value={details.contactEmail}
                onChange={(event) =>
                  updateDetail("contactEmail", event.target.value)
                }
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <TextField
                fullWidth
                type="tel"
                label={t("phone")}
                value={details.contactPhone}
                onChange={(event) =>
                  updateDetail("contactPhone", event.target.value)
                }
              />
            </Grid>
          </Grid>
        </DetailSection>

        {changesMade && (
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5 }}>
            <Button variant="outlined" onClick={clearChanges}>
              {t("cancelChanges")}
            </Button>
            <Button
              variant="contained"
              disabled={isSaving}
              onClick={saveChanges}>
              {t("update")}
            </Button>
          </Box>
        )}

        <DetailSection
          title={t("centerManagers")}
          action={
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              disabled={!center}
              onClick={() => setPersonDialogType("manager")}>
              {t("addManager")}
            </Button>
          }>
          <PeopleTable
            rows={centerManagers}
            columns={managerColumns}
            loading={peopleLoading}
            emptyLabel={t("noCenterManagers")}
          />
        </DetailSection>

        <DetailSection
          title={t("monitors")}
          action={
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              disabled={!center}
              onClick={() => setPersonDialogType("monitor")}>
              {t("addMonitor")}
            </Button>
          }>
          <PeopleTable
            rows={monitors}
            columns={monitorColumns}
            loading={peopleLoading}
            emptyLabel={t("noMonitors")}
          />
        </DetailSection>

        {center && centerManagers.length === 0 && (
          <Box>
            <Button
              color="error"
              variant="outlined"
              onClick={handleDeleteCenter}>
              {t("deleteCenter")}
            </Button>
          </Box>
        )}
      </Box>

      <UpdateCenterAddressDialog
        open={Boolean(addressMode)}
        title={
          addressMode === "sample"
            ? t("samplePickupAddress")
            : t("kitOrderAddress")
        }
        address={addressDraft}
        onChange={updateAddressDraft}
        onClose={closeAddressDialog}
        onSave={saveAddress}
      />
      <AddCenterPersonDialog
        open={Boolean(personDialogType)}
        centerId={center?.id}
        personType={personDialogType}
        saving={personSaving}
        onClose={() => setPersonDialogType(null)}
        onAdd={handleAddCenterPerson}
      />
      <CenterForm
        visible={centerFormOpen}
        center={center}
        studyId={center?.studyId || studyId}
        onClose={(refresh) => {
          setCenterFormOpen(false);
          if (refresh && centerId) {
            store.dispatch(getCenter(centerId));
          }
        }}
      />
    </Grid>
  );
};
